/**
 * PitchArrowsOverlay (EPIC-110 D2): three semi-transparent lane arrows drawn
 * ON a pitch, pointing in the team's attacking direction. Mount it inside the
 * element that the period rotation applies to: the arrows are then drawn in
 * the pitch's own frame (home right, away left) and rotate with it, so
 * `data-direction` (the on-screen direction, equal to that chart's
 * `AttackDirectionCaption` arrow) is flipped when `reversed`.
 */
import type { TeamSide } from "../../utils/teamControlMap";

interface PitchArrowsOverlayProps {
  side: TeamSide;
  /** True when the host pitch is rendered rotated 180 degrees. */
  reversed?: boolean;
  /** "all" (default): three lane arrows. "outer": top and bottom lanes only,
   * so nothing is drawn through labels centred on the pitch (thirds pitch). */
  lanes?: "all" | "outer";
  "data-testid"?: string;
}

const LANE_Y = [13.3, 40, 66.7];

export default function PitchArrowsOverlay({
  side,
  reversed = false,
  lanes = "all",
  "data-testid": testId,
}: PitchArrowsOverlayProps) {
  const effectiveSide = reversed ? (side === "home" ? "away" : "home") : side;
  const dir = side === "home" ? 1 : -1; // drawn in the pitch's own frame
  const x0 = dir === 1 ? 42 : 78;
  const x1 = dir === 1 ? 78 : 42;
  return (
    <svg
      viewBox="-2 -2 124 84"
      className="absolute inset-0 h-full w-full pointer-events-none"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      data-testid={testId}
      data-direction={effectiveSide === "home" ? "right" : "left"}
    >
      <g
        stroke="rgba(255,255,255,0.4)"
        strokeWidth={1.2}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      >
        {(lanes === "outer" ? [LANE_Y[0], LANE_Y[2]] : LANE_Y).map((y) => (
          <g key={y} data-lane-arrow="true">
            <line x1={x0} y1={y} x2={x1} y2={y} />
            <polyline
              points={`${x1 - dir * 4},${y - 3.5} ${x1},${y} ${x1 - dir * 4},${y + 3.5}`}
            />
          </g>
        ))}
      </g>
    </svg>
  );
}
